import {useFeatureIsOn} from "@growthbook/growthbook-react";
import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import type {Result} from "@khanacademy/wonder-blocks-data";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {Caption, HeadingSmall} from "@khanacademy/wonder-blocks-typography";
import {usePublishAnalyticsEvent} from "@repo/analytics-learning-time";
import {useMutation, type MutateFn} from "@repo/data-gql";
import {logUnreachableCode} from "@repo/logging";

import SchoolSelector from "#auth/components/school-selector/school-selector.tsx";
import type {School} from "#auth/components/school-selector/types.ts";

import type {
    setTeacherCountryAffiliation as SetCountryData,
    setTeacherCountryAffiliationVariables as SetCountryVariables,
} from "./__graphql-types__/setTeacherCountryAffiliation.ts";
import type {
    setTeacherSchoolAffiliation as SetSchoolData,
    setTeacherSchoolAffiliationVariables as SetSchoolVariables,
} from "./__graphql-types__/setTeacherSchoolAffiliation.ts";
import {
    canSaveCountryAndSchool,
    mapPromise,
    schoolPickerSupported,
} from "./school-section-helpers.ts";
import {
    TEACHER_COUNTRY_AFFILIATION_MUTATION,
    TEACHER_SCHOOL_AFFILIATION_MUTATION,
} from "./school-section-mutations.ts";

type Props = {
    affiliationCountryCode: string | null | undefined;
    schoolAffiliation: School | null | undefined;
};

type FormFields = {
    countryCode: string | null | undefined;
    school: School | null | undefined;
};

function runMutation(
    setCountryMutation: MutateFn<SetCountryData, SetCountryVariables>,
    setSchoolMutation: MutateFn<SetSchoolData, SetSchoolVariables>,
    vietnamSchoolPickerOn: boolean,
    kenyaSchoolPickerOn: boolean,
    countryCode?: string | null,
    school?: School | null,
): Promise<Result<FormFields>> {
    if (!countryCode) {
        return Promise.resolve({
            status: "error",
            error: new Error("Expect non-null countryCode"),
        });
    }

    if (
        !schoolPickerSupported(
            countryCode,
            vietnamSchoolPickerOn,
            kenyaSchoolPickerOn,
        )
    ) {
        const result = setCountryMutation({
            countryCode: countryCode,
        });
        return mapPromise(result, {
            countryCode,
            school: null,
        });
    }

    if (!school) {
        return Promise.resolve({
            status: "error",
            error: new Error("Expect non-null School"),
        });
    }

    const result = setSchoolMutation({
        countryCode: countryCode,
        eduorgKeyId: school.id,
    });
    return mapPromise(result, {
        countryCode,
        school,
    });
}

// TODO(bcdirito, CLASS-10198)
// Write spec tests covering behavior
// Must complete before deployment
export default function SchoolSection({
    schoolAffiliation,
    affiliationCountryCode,
}: Props): React.ReactElement {
    const vietnamSchoolPickerOn = useFeatureIsOn("vietnam-school-picker");
    const kenyaSchoolPickerOn = useFeatureIsOn("kenya-school-picker");

    // The school variable tracks the current state (in the UI).
    const [school, setSchool] = React.useState<School | null | undefined>(
        schoolAffiliation,
    );
    // lastSavedSchool tracks the state that is saved on the server.
    // It is also initialized by the schoolAffiliation prop.
    // It is set only on a successful mutation, and compared against school to
    // determine whether to enable the save button.
    const [lastSavedSchool, setLastSavedSchool] =
        React.useState(schoolAffiliation);

    // The countryCode variable tracks the current selection in the dropdown.
    // It is initialized by the affiliationCountryCode prop.
    // NOTE: We need to initialize to US if null since that is auto-selected.
    const [countryCode, setCountryCode] = React.useState<
        string | null | undefined
    >(affiliationCountryCode ?? "US");
    // lastSavedCountryCode tracks the state that is saved on the server.
    // It is also initialized by the affiliationCountryCode prop.
    // It is set only on a successful mutation, and compared against
    // countryCode to determine whether to enable the save button.
    const [lastSavedCountryCode, setLastSavedCountryCode] = React.useState(
        affiliationCountryCode,
    );

    // Tracks whether the mutation triggered by clicking the save button is
    // loading.
    const [isSaveLoading, setSaveLoading] = React.useState(false);

    // Tracks whether the mutation triggered by clicking the save button
    // encountered an error state.
    const [isSaveError, setSaveError] = React.useState(false);

    const publishAnalyticsEvent = usePublishAnalyticsEvent();

    // There are three mutations that may be used depending on which country
    // or school is selected. We need to call useMutation on each of them here.
    // The runMutation function will call the appropriate one.
    const [setCountryMutation] = useMutation<
        SetCountryData,
        SetCountryVariables
    >(TEACHER_COUNTRY_AFFILIATION_MUTATION);
    const [setSchoolMutation] = useMutation<SetSchoolData, SetSchoolVariables>(
        TEACHER_SCHOOL_AFFILIATION_MUTATION,
    );

    return (
        <View role="region" aria-label={t`School settings`}>
            <HeadingSmall style={styles.header} tag="h2">
                {t`Add your school`}
            </HeadingSmall>
            <Strut size={spacing.medium_16} />
            <SchoolSelector
                selectedSchool={school}
                selectedCountryCode={countryCode}
                onSchoolChanged={(school) => setSchool(school)}
                onCountryChanged={(country) => {
                    // Clear out school to avoid bug in SchoolSelector
                    setSchool(null);
                    setCountryCode(country);
                }}
            />
            <Button
                onClick={async () => {
                    const result = await runMutation(
                        setCountryMutation,
                        setSchoolMutation,
                        vietnamSchoolPickerOn,
                        kenyaSchoolPickerOn,
                        countryCode,
                        school,
                    );
                    switch (result.status) {
                        case "success":
                            popSuccessToast();
                            setSaveLoading(false);
                            setSaveError(false);
                            setLastSavedCountryCode(result.data.countryCode);
                            setLastSavedSchool(result.data.school);
                            publishAnalyticsEvent({
                                eventType: "SchoolPickerSchoolSaved",
                                eventSchemaVersion: 2,
                            });
                            break;
                        case "aborted":
                        case "error":
                            setSaveLoading(false);
                            setSaveError(true);
                            break;
                        case "no-data":
                        case "loading":
                            setSaveLoading(true);
                            setSaveError(false);
                            break;
                        default:
                            throw logUnreachableCode(
                                "Unknown progress state",
                                // @ts-expect-error - TS2345 - Argument of type 'any' is not assignable to parameter of type 'never'. | TS2339 - Property 'status' does not exist on type 'never'.
                                result.status,
                            );
                    }
                }}
                style={styles.button}
                disabled={
                    !canSaveCountryAndSchool(
                        vietnamSchoolPickerOn,
                        kenyaSchoolPickerOn,
                        countryCode,
                        school,
                        lastSavedCountryCode,
                        lastSavedSchool,
                    ) && !isSaveLoading
                }
                testId="school-section-save-button"
            >
                {t`Save changes`}
            </Button>
            <div aria-live="polite">
                {isSaveError && (
                    <Caption style={styles.error}>
                        {t`Failed to save changes`}
                    </Caption>
                )}
            </div>
        </View>
    );
}

const popSuccessToast = () => {
    import("../notifications-package/toast-notification.tsx").then(
        ({renderChangesSavedToast}) => {
            renderChangesSavedToast();
        },
    );
};

const styles = StyleSheet.create({
    header: {
        color: color.offBlack,
    },
    button: {
        alignSelf: "flex-start",
        paddingLeft: spacing.medium_16,
        paddingRight: spacing.medium_16,
    },
    error: {
        color: color.red,
        height: "100%",
        marginLeft: spacing.small_12,
        // This matches the margin on the button. See the note above for why
        // we're not using a WonderBlocks component.
        marginTop: 10,
    },
});
